import { format } from "date-fns";
import Input from "immigram-ui-cmon/src/components/Input/Input";
import React, { useContext, useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

import { isAdmin } from "@/lib/auth/rbac";
import { UserContext } from "@/lib/contexts/userContext";
import useTransparentState from "@/lib/hook/useTransparentState";
import { ARRAY_FIELD_NAME } from "@/lib/hookForm";

import { VoiceRecorderButton } from "./VoiceRecorderButton";

interface ControlledInputProps<T extends FieldValues> {
  control: Control<T>;
  index?: number;
  fieldName: string;
  label?: React.ReactNode;
  description?: string;
  rules?: Record<string, any>;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  error?: string;
  enableVoiceRecorder?: boolean;
  onHelpClick?: () => void;
  onChange?: (
    fieldName: string,
    value: string,
    arrayFieldIndex?: number
  ) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const ControlledInputWithRaisingLabel = <
  T extends Record<string, any> = Record<string, any>
>(
  props: ControlledInputProps<T>
) => {
  const ctx = useContext(UserContext);

  const canToggleDisabled = props.disabled && isAdmin(ctx.ownUser);
  const [disabledState, setDisabledState] = useTransparentState(props.disabled);

  // Используем useState на уровне компонента для хранения состояния транскрипции по ключам полей
  const fieldKey =
    props.index === undefined
      ? props.fieldName
      : `${ARRAY_FIELD_NAME}.${props.index}.${props.fieldName}`;
  const [isTranscribingMap, setIsTranscribingMap] = useState<
    Record<string, boolean>
  >({});

  const isTranscribing = isTranscribingMap[fieldKey] || false;
  const setIsTranscribing = (value: boolean) => {
    setIsTranscribingMap((prev) => ({ ...prev, [fieldKey]: value }));
  };

  return (
    <Controller
      control={props.control}
      rules={props.rules}
      name={
        (props.index === undefined
          ? props.fieldName
          : `${ARRAY_FIELD_NAME}.${props.index}.${props.fieldName}`) as Path<T> // TODO: Fix generics for fieldName
      }
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        let resultValue: string = value ? value : "";
        if (props.type === "date" && value) {
          resultValue = format(new Date(value), "yyyy-MM-dd");
        }

        // Голосовой ввод включаем ТОЛЬКО для обычных текстовых полей.
        // Не включаем для url/email/tel/number/date/hidden и т.п.
        const isTextType =
          props.type === undefined ||
          props.type === "text" ||
          props.type === "search";
        const enableVoiceRecorder =
          props.enableVoiceRecorder !== false && isTextType && !disabledState;

        return (
          <Input
            name={props.fieldName}
            label={props.label}
            description={props.description}
            type={props.type}
            disabled={disabledState}
            onDisabledToggle={canToggleDisabled ? setDisabledState : undefined}
            value={resultValue}
            error={error?.message ?? props.error}
            enableVoiceRecorder={enableVoiceRecorder}
            isTranscribing={isTranscribing}
            voiceRecorderComponent={
              enableVoiceRecorder ? (
                <VoiceRecorderButton
                  onTranscriptionComplete={(text) => {
                    onChange(text);
                    if (props.onChange) {
                      props.onChange(props.fieldName, text, props.index);
                    }
                  }}
                  disabled={disabledState}
                  currentValue={resultValue || ""}
                  onTranscribingChange={setIsTranscribing}
                  fieldName={props.fieldName}
                />
              ) : undefined
            }
            onChange={(value) => {
              onChange(value);
              if (props.onChange) {
                props.onChange(props.fieldName, value, props.index);
              }
            }}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
          />
        );
      }}
    />
  );
};

export default ControlledInputWithRaisingLabel;
